import { ITEM_TYPES } from '../constants';
import { debtPaymentDue, liquidMonthlyInterest, monthlyAmount } from './engine';

// The cashflow diagram needs each item split into money coming *in* and money
// going *out*, which the kernel's net-scalar itemMonthlyCashflow cannot
// express — hence its own traversal. The amounts it draws still come from the
// kernel, so an arrow here and a KPI on the dashboard agree by construction.

// Arrows thinner than this are visual noise and get dropped.
const MIN_FLOW = 0.001;

export const generateSankeyData = ({ activeItems, liquidAssets, liquidAssetsInterestRate }) => {
    const finalNodes = [];
    const nodeKeyToIndexMap = new Map();
    const links = [];
    const centralNodeKey = 'sankey.cashflowPool';

    const getOrAddNodeIndex = (key, type, value = null) => {
        if (!nodeKeyToIndexMap.has(key)) {
            // A role, not a colour. This used to emit theme-specific hex, which
            // put presentation in the calculation layer — and the golden master
            // recorded it, so restyling the chart broke a calculation baseline.
            // SankeyChart maps these to the palette.
            let role;
            if (key === centralNodeKey) role = 'pool';
            else if (type === 'income_source') role = 'in';
            else if (type === 'expense_sink') role = 'out';
            else role = 'other';
            finalNodes.push({ nameKey: key, nameValue: value, role });
            nodeKeyToIndexMap.set(key, finalNodes.length - 1);
        }
        return nodeKeyToIndexMap.get(key);
    };

    const centralNodeIndex = getOrAddNodeIndex(centralNodeKey, "pool");

    const addLink = (key, type, label, amount, direction) => {
        if (!(amount > MIN_FLOW) || !key) return;
        const nodeIndex = getOrAddNodeIndex(key, type, label);
        links.push(direction === 'in'
            ? { source: nodeIndex, target: centralNodeIndex, value: amount }
            : { source: centralNodeIndex, target: nodeIndex, value: amount });
    };

    // --- money in ----------------------------------------------------------
    activeItems.forEach(item => {
        const label = item.name || '';
        switch (item.type) {
            case ITEM_TYPES.INCOME:
                addLink(item.name, 'income_source', null, monthlyAmount(item.monthlyIncome, item.amountFrequency), 'in');
                break;
            case ITEM_TYPES.REAL_ESTATE:
                if (item.isRented) {
                    addLink('sankey.rent', 'income_source', label, monthlyAmount(item.monthlyRentalIncome, item.amountFrequency), 'in');
                }
                break;
            case ITEM_TYPES.OTHER_INVESTMENT: {
                if (item.isIncome) {
                    const yieldAmount = (parseFloat(item.currentValue || 0) * parseFloat(item.annualIncomeYield || 0)) / 12;
                    addLink('sankey.yield', 'income_source', label, yieldAmount, 'in');
                }
                // A negative contribution is a withdrawal — money leaving the
                // investment and arriving in the cashflow pool.
                const contribution = monthlyAmount(item.monthlyContribution, item.contributionFrequency);
                if (contribution < 0) {
                    addLink('sankey.withdrawal', 'income_source', label, Math.abs(contribution), 'in');
                }
                break;
            }
            default:
                break;
        }
    });

    addLink('sankey.interestIncome', 'income_source', null, liquidMonthlyInterest(liquidAssets, liquidAssetsInterestRate), 'in');

    // --- money out ---------------------------------------------------------
    activeItems.forEach(item => {
        const label = item.name || '';
        switch (item.type) {
            case ITEM_TYPES.EXPENSE:
                addLink(item.name, 'expense_sink', null, monthlyAmount(item.monthlyExpense, item.amountFrequency), 'out');
                break;
            case ITEM_TYPES.REAL_ESTATE: {
                if (!item.isRented) {
                    addLink('sankey.costs', 'expense_sink', label, monthlyAmount(item.monthlyCosts, item.amountFrequency), 'out');
                }
                addLink('sankey.mortgage', 'expense_sink', label, debtPaymentDue(item), 'out');
                break;
            }
            case ITEM_TYPES.LOAN:
                addLink('sankey.loanPayment', 'expense_sink', label, debtPaymentDue(item), 'out');
                break;
            case ITEM_TYPES.OTHER_INVESTMENT: {
                const contribution = monthlyAmount(item.monthlyContribution, item.contributionFrequency);
                if (contribution > 0) addLink('sankey.contribution', 'expense_sink', label, contribution, 'out');
                break;
            }
            default:
                break;
        }
    });

    return { nodes: finalNodes, links: links.filter(link => link.value > MIN_FLOW) };
};
